import { type FC } from 'react';

import { cx } from '@emotion/css';
import { Close, Info, Minimize } from '@livechat/design-system-icons';
import { Button, Icon, Text, Tooltip } from '@livechat/design-system-react-components';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { CopilotTrackEvent } from 'constants/copilot-event';
import { EventPlace } from 'helpers/analytics';
import { trackEvent } from 'services/event-tracking';
import { getCopilotAllEvents } from 'store/entities/copilot/selectors';
import { CopilotViewActions } from 'store/views/copilot/actions';
import { getIsCopilotDetailsOpened } from 'store/views/copilot/selectors';

import {
  HEADER_TOOLTIP_HOVER_DELAY,
  COPILOT_CLOSE_MODAL_TEST_ID,
  COPILOT_MODAL_DETAILS_BTN_TEST_ID,
  COPILOT_TOGGLE_MODAL_BTN_TEST_ID,
} from './constants';

import * as styles from './styles';

export const CopilotFullscreenHeader: FC = () => {
  const dispatch = useDispatch();
  const isDetailsOpened = useSelector(getIsCopilotDetailsOpened);
  const location = useLocation();
  const copilotAllEvents = useSelector(getCopilotAllEvents);

  const handleOpenDetails = (): void => {
    dispatch(CopilotViewActions.openDetails());
    trackEvent(CopilotTrackEvent.OpenDetails, EventPlace.One);
  };

  const handleCloseDetails = (): void => {
    dispatch(CopilotViewActions.closeDetails());
    trackEvent(CopilotTrackEvent.CloseDetails, EventPlace.One);
  };

  const handleCloseModal = (): void => {
    dispatch(CopilotViewActions.hideModal());
    dispatch(CopilotViewActions.resetCopilotUnreadMessagesCount());
    trackEvent(CopilotTrackEvent.ChatClosed, EventPlace.One, {
      state: 'fullscreen',
      url: location?.pathname,
      messages: Object.keys(copilotAllEvents).length,
    });
  };

  const toggleModal = (): void => {
    dispatch(CopilotViewActions.toggleExpand());
    trackEvent(CopilotTrackEvent.Resized, EventPlace.One, { resizedTo: 'popover' });
  };

  return (
    <div className={styles.headerWrapperExpanded}>
      <div>
        <Text as="h2">Copilot</Text>
      </div>

      <div>
        <Tooltip
          hoverOnDelay={HEADER_TOOLTIP_HOVER_DELAY}
          kind="invert"
          triggerRenderer={
            <Button
              className={cx({ [styles.activeDetailsButton]: isDetailsOpened })}
              kind="plain"
              onClick={isDetailsOpened ? handleCloseDetails : handleOpenDetails}
              icon={<Icon className={styles.icon} source={Info} />}
              data-testid={COPILOT_MODAL_DETAILS_BTN_TEST_ID}
            />
          }
        >
          <Text>Details</Text>
        </Tooltip>

        <Tooltip
          hoverOnDelay={HEADER_TOOLTIP_HOVER_DELAY}
          kind="invert"
          triggerRenderer={
            <Button
              kind="plain"
              onClick={toggleModal}
              icon={<Icon className={styles.icon} source={Minimize} />}
              data-testid={COPILOT_TOGGLE_MODAL_BTN_TEST_ID}
            />
          }
        >
          <Text>Collapse</Text>
        </Tooltip>

        <Tooltip
          hoverOnDelay={HEADER_TOOLTIP_HOVER_DELAY}
          kind="invert"
          triggerRenderer={
            <Button
              kind="plain"
              onClick={handleCloseModal}
              icon={<Icon className={styles.icon} source={Close} />}
              data-testid={COPILOT_CLOSE_MODAL_TEST_ID}
            />
          }
        >
          <Text>Close</Text>
        </Tooltip>
      </div>
    </div>
  );
};
